import React, { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight, Linkedin } from "lucide-react";
import { CONTACT, CONTACT_INFO } from "../data/mock";
import Reveal from "../components/Reveal";
import { useToast } from "../hooks/use-toast";

const Field = ({ label, name, type = "text", required, value, onChange, textarea }) => (
  <div>
    <label className="block text-sm text-[#55585f] mb-2">
      {label} {required && <span className="text-[var(--gold)]">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        rows={5}
        className="w-full bg-white border border-black/10 rounded-sm px-4 py-3 text-[#14161c] outline-none focus:border-[var(--gold)] transition-colors resize-none"
      />
    ) : (
      <input
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full bg-white border border-black/10 rounded-sm px-4 py-3 text-[#14161c] outline-none focus:border-[var(--gold)] transition-colors"
      />
    )}
  </div>
);

const Contact = () => {
  const d = CONTACT;
  const { toast } = useToast();
  const [mode, setMode] = useState("hiring");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const entry = { ...form, mode, at: new Date().toISOString() };
    const prev = JSON.parse(localStorage.getItem("pt_enquiries") || "[]");
    localStorage.setItem("pt_enquiries", JSON.stringify([entry, ...prev]));
    toast({
      title: "Enquiry sent",
      description: "Thank you — Preeti will personally reply within one business day.",
    });
    setForm({ name: "", email: "", phone: "", company: "", message: "" });
  };

  const info = [
    { icon: Mail, label: "Email", value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
    { icon: Phone, label: "Phone", value: CONTACT_INFO.phone, href: `tel:${CONTACT_INFO.phoneHref}` },
    { icon: MapPin, label: "Location", value: CONTACT_INFO.location },
    { icon: Linkedin, label: "LinkedIn", value: "PT Hiring Solutions", href: CONTACT_INFO.linkedin, external: true },
  ];

  return (
    <div>
      <section className="grain bg-[#16233f] text-white pt-40 pb-20 md:pt-48">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <Reveal><div className="eyebrow mb-7">{d.eyebrow}</div></Reveal>
          <Reveal delay={80}>
            <h1 className="font-serif font-semibold leading-[1.02] text-[42px] md:text-[72px]">
              {d.titleLines[0]}<br />
              <span className="italic text-[var(--gold)]">{d.titleLines[1]}</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-white/65 text-lg leading-relaxed max-w-2xl mt-8">{d.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[var(--cream)] py-24 md:py-28">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 grid lg:grid-cols-[0.85fr_1.15fr] gap-14 lg:gap-20">
          {/* info */}
          <div>
            <div className="space-y-8">
              {info.map((it) => (
                <Reveal key={it.label}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-sm bg-[#16233f] flex items-center justify-center shrink-0">
                      <it.icon size={20} className="text-[var(--gold)]" />
                    </div>
                    <div>
                      <div className="eyebrow mb-1">{it.label}</div>
                      {it.href ? (
                        <a
                          href={it.href}
                          {...(it.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="text-[#14161c] hover:text-[var(--gold)] transition-colors break-all"
                        >
                          {it.value}
                        </a>
                      ) : (
                        <span className="text-[#14161c]">{it.value}</span>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* form */}
          <Reveal delay={120}>
            <form onSubmit={onSubmit} className="bg-white rounded-sm p-8 md:p-10 shadow-[0_30px_60px_-30px_rgba(20,22,28,0.25)] border border-black/5">
              <div className="flex gap-2 mb-8 bg-[var(--cream-2)] p-1 rounded-sm w-fit">
                {[{ k: "hiring", l: "I'm hiring" }, { k: "seeking", l: "I'm job seeking" }].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setMode(t.k)}
                    className={`px-5 py-2.5 rounded-sm text-sm font-medium transition-all ${
                      mode === t.k ? "bg-[#16233f] text-white" : "text-[#55585f] hover:text-[#14161c]"
                    }`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name" name="name" required value={form.name} onChange={onChange} />
                <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                <Field label="Company" name="company" value={form.company} onChange={onChange} />
              </div>
              <div className="mt-5">
                <Field label="Message" name="message" required textarea value={form.message} onChange={onChange} />
              </div>

              <button type="submit" className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-sm mt-8">
                Send enquiry <ArrowRight size={18} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
